import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Package, Users, BarChart3, Settings, LogOut, UserCog, Shield, ChevronDown, ChevronRight, Route, Menu, X, FolderOpen, Warehouse, ShoppingBag, Truck, FileText, Cloud, Loader, Tag, RotateCcw } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useSync } from '../../context/SyncContext';
import { getVisibleViews, STORAGE_KEY } from '../settings/WebModeConfig';
import apiService from '../../services/apiService';
import './Sidebar.css';

const Sidebar = ({ activeView, onViewChange }) => {
  const { user, logout, sidebarCollapsed, setSidebarCollapsed, toggleSidebar, isWebMode } = useApp();
  const [empresaLogo, setEmpresaLogo] = useState(null);
  const [empresaNombre, setEmpresaNombre] = useState('');

  // Cargar logo de la empresa
  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const result = await apiService.getConfiguracionesPorCategoria('empresa', null);
        if (result.success && result.data?.valores) {
          setEmpresaLogo(result.data.valores.empresa_logo || null);
          setEmpresaNombre(result.data.valores.empresa_nombre || '');
        }
      } catch (e) {
        // silencioso
      }
    };
    cargarLogo();
  }, []);
  const {
    hasPermission,
    canProcessSales,
    canManageProducts,
    canManageUsers,
    canManageRoles,
    canAccessReports,
    hasAnyPermission
  } = usePermissions();

  const { active: syncActive, syncing: syncRunning, lastResult: syncLastResult, syncConfig, setShowModal: setSyncModal } = useSync();

  // Configuración de vistas visibles en modo web
  const [webVisibleViews, setWebVisibleViews] = useState(getVisibleViews());

  // Escuchar cambios en la configuración de modo web
  useEffect(() => {
    const handleConfigChange = () => setWebVisibleViews(getVisibleViews());
    window.addEventListener('webmode-config-changed', handleConfigChange);
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) handleConfigChange();
    });
    return () => {
      window.removeEventListener('webmode-config-changed', handleConfigChange);
      window.removeEventListener('storage', handleConfigChange);
    };
  }, []);

  // Estado para controlar items colapsables
  const [collapsedItems, setCollapsedItems] = useState({});

  // Ref para rastrear la vista anterior y evitar auto-colapso repetido
  const prevViewRef = useRef(null);

  // Usar el estado del contexto para el colapso del sidebar
  const isSidebarCollapsed = sidebarCollapsed;
  const setIsSidebarCollapsed = setSidebarCollapsed;

  // Colapsar automáticamente el sidebar SOLO al cambiar a ciertas vistas
  useEffect(() => {
    const viewsToCollapse = ['ventas-mostrador', 'ventas-mostrador-v2', 'cambio-precios', 'precios-ray'];
    // Solo colapsar si la vista cambió (no por re-render del contexto)
    if (activeView !== prevViewRef.current) {
      prevViewRef.current = activeView;
      if (viewsToCollapse.includes(activeView)) {
        setIsSidebarCollapsed(true);
      }
    }
  }, [activeView, setIsSidebarCollapsed]);

  // Definir elementos del menú con sus permisos requeridos
  const allMenuItems = [
    {
      id: 'sales',
      label: 'Ventas',
      icon: ShoppingCart,
      permission: 'ventas_ver',
      checkPermission: () => canProcessSales(),
      collapsible: true,
      children: [
        {
          id: 'ventas-mostrador',
          label: 'Ventas de mostrador',
          permission: 'ventas_mostrador_ver',
          checkPermission: () => hasPermission('ventas_mostrador_ver'),
          electronOnly: true
        },

        {
          id: 'corte-caja',
          label: 'Corte de Caja',
          permission: 'corte_caja_ver',
          checkPermission: () => hasPermission('corte_caja_ver'),
          electronOnly: true
        },
        {
          id: 'pedidos',
          label: 'Pedidos',
          permission: 'pedidos_ver',
          checkPermission: () => hasPermission('pedidos_ver')
        },
        {
          id: 'cuentas-por-cobrar',
          label: 'Cuentas por Cobrar',
          permission: 'cxc_ver',
          checkPermission: () => hasPermission('cxc_ver')
        },
        {
          id: 'devoluciones',
          label: 'Devoluciones',
          icon: RotateCcw,
          permission: 'devoluciones_ver',
          checkPermission: () => hasPermission('devoluciones_ver'),
          electronOnly: true
        }
      ]
    },

    {
      id: 'compras',
      label: 'Compras',
      icon: ShoppingBag,
      permission: 'compras_ver',
      checkPermission: () => hasPermission('compras_ver') || hasPermission('ordenes_compra_ver') || hasPermission('cxp_ver'),
      collapsible: true,
      children: [
        {
          id: 'ordenes-compra',
          label: 'Órdenes de Compra',
          permission: 'ordenes_compra_ver',
          checkPermission: () => hasPermission('ordenes_compra_ver')
        },
        {
          id: 'cuentas-por-pagar',
          label: 'Cuentas por Pagar',
          permission: 'cxp_ver',
          checkPermission: () => hasPermission('cxp_ver')
        }
      ]
    },
    {
      id: 'catalogos',
      label: 'Catálogos',
      icon: FolderOpen,
      permission: 'catalogos_ver',
      checkPermission: () => hasPermission('catalogos_ver') || hasPermission('proveedores_ver'),
      collapsible: true,
      children: [
        {
          id: 'almacenes',
          label: 'Almacenes',
          permission: 'almacenes_ver',
          checkPermission: () => hasPermission('almacenes_ver')
        },
        {
          id: 'products',
          label: 'Productos',
          permission: 'productos_ver',
          checkPermission: () => canManageProducts()
        },
        {
          id: 'migracion-productos',
          label: 'Migrar Productos',
          permission: 'productos_ver',
          checkPermission: () => hasPermission('productos_ver')
        },
        {
          id: 'cambio-precios',
          label: 'Cambio de Precios',
          permission: 'productos_editar',
          checkPermission: () => hasPermission('productos_editar')
        },
        {
          id: 'precios-ray',
          label: 'Precios Ray',
          permission: 'precios_ray',
          checkPermission: () => hasPermission('precios_ray')
        },
        {
          id: 'etiquetas-zebra',
          label: 'Etiquetas Zebra',
          permission: 'etiquetas_zebra',
          checkPermission: () => hasPermission('etiquetas_zebra'),
          electronOnly: true
        },
        {
          id: 'customers',
          label: 'Clientes',
          permission: 'clientes_ver',
          checkPermission: () => hasPermission('clientes_ver')
        },
        {
          id: 'proveedores',
          label: 'Proveedores',
          permission: 'proveedores_ver',
          checkPermission: () => hasPermission('proveedores_ver')
        }
      ]
    },
    {
      id: 'almacen',
      label: 'Almacén',
      icon: Package,
      permission: 'almacen_ver',
      checkPermission: () => hasPermission('almacen_ver'),
      collapsible: true,
      children: [
        {
          id: 'picking',
          label: 'Picking',
          permission: 'picking_ver',
          checkPermission: () => hasPermission('picking_ver'),
          electronOnly: true
        },
        {
          id: 'preparacion',
          label: 'Preparación',
          permission: 'preparacion_ver',
          checkPermission: () => hasPermission('preparacion_ver'),
          electronOnly: true
        }
      ]
    },
    {
      id: 'logistica',
      label: 'Logística',
      icon: Route,
      permission: 'logistica_ver',
      checkPermission: () => hasPermission('logistica_ver'),
      collapsible: true,
      children: [
        {
          id: 'medido-hoy',
          label: 'Pedidos de hoy',
          permission: 'medido_hoy_ver',
          checkPermission: () => hasPermission('medido_hoy_ver')
        },
        {
          id: 'caratula-pedidos',
          label: 'Carátula de Pedidos',
          permission: 'caratula_pedidos_ver',
          checkPermission: () => hasPermission('caratula_pedidos_ver')
        }
      ]
    },
    {
      id: 'flete',
      label: 'Gestión de Flete',
      icon: Route,
      permission: 'flete_ver',
      checkPermission: () => hasPermission('flete_ver')
    },
    {
      id: 'facturacion',
      label: 'Facturación',
      icon: FileText,
      permission: 'facturacion_ver',
      checkPermission: () => hasPermission('facturacion_ver')
    },
    {
      id: 'user-management',
      label: 'Gestión de Usuarios',
      icon: UserCog,
      permission: 'gestion_usuarios_ver',
      checkPermission: () => hasPermission('gestion_usuarios_ver'),
      collapsible: true,
      children: [
        {
          id: 'users',
          label: 'Usuarios',
          permission: 'usuarios_ver',
          checkPermission: () => canManageUsers()
        },
        {
          id: 'roles',
          label: 'Roles',
          permission: 'roles_ver',
          checkPermission: () => canManageRoles()
        }
      ]
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: Settings,
      permission: 'configuracion_ver',
      checkPermission: () => hasAnyPermission(['configuracion_ver', 'configuracion_admin'])
    },
    {
      id: 'reports',
      label: 'Reportes',
      icon: BarChart3,
      permission: 'reportes_ventas_ver',
      checkPermission: () => canAccessReports() || hasPermission('reporte_ventas_mostrador'),
      collapsible: true,
      children: [
        {
          id: 'reports',
          label: 'Reporte de Ventas',
          permission: 'reportes_ventas_ver',
          checkPermission: () => canAccessReports()
        },
        {
          id: 'reporte-ventas-mostrador',
          label: 'Ventas de Mostrador',
          permission: 'reporte_ventas_mostrador',
          checkPermission: () => hasPermission('reporte_ventas_mostrador')
        }
      ]
    }
  ];

  // Verificar si un view es visible en el modo actual
  const isViewVisible = (item) => {
    if (!isWebMode) return true; // En Electron todo es visible
    // En modo web, verificar si el view está en la lista de visibles
    if (item.id && !item.children) {
      return webVisibleViews.includes(item.id);
    }
    return true; // Los grupos se muestran si tienen al menos un hijo visible
  };

  // Función recursiva para verificar permisos en cualquier nivel
  const hasAnyChildWithPermission = (item) => {
    if (isWebMode && !isViewVisible(item)) return false;
    if (item.checkPermission && item.checkPermission()) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => hasAnyChildWithPermission(child));
    }
    return false;
  };

  // Filtrar elementos del menú basado en permisos y modo
  const menuItems = allMenuItems.filter(item => {
    if (isWebMode && !isViewVisible(item)) return false;
    if (item.children) {
      const hasChildrenWithPermission = item.children.some(child => hasAnyChildWithPermission(child));
      return hasChildrenWithPermission;
    }
    return item.checkPermission();
  });

  // Función para alternar el estado colapsado
  const toggleCollapse = (itemId) => {
    setCollapsedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirigir al login después del logout
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Función recursiva para renderizar sub-items anidados
  const renderSubItems = (children, level = 1) => {
    return children
      .filter(child => isViewVisible(child))
      .filter(child => hasAnyChildWithPermission(child))
      .map((child) => {
        const isCollapsed = collapsedItems[child.id];
        const hasChildren = child.children && child.children.length > 0;

        return (
          <div key={child.id}>
            <button
              className={`sidebar-subitem ${hasChildren ? 'collapsible' : ''} ${activeView === child.id ? 'active' : ''} level-${level}`}
              onClick={() => {
                if (hasChildren) {
                  toggleCollapse(child.id);
                } else {
                  onViewChange(child.id);
                }
              }}
              title={`${child.label} - Requiere: ${child.permission}`}
              style={{ paddingLeft: `${1.25 + (level * 0.75)}rem` }}
            >
              <span>{child.label}</span>
              {hasChildren && (
                <div className="collapse-icon-small">
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </div>
              )}
            </button>
            {hasChildren && !isCollapsed && (
              <div className="sidebar-submenu-nested">
                {renderSubItems(child.children, level + 1)}
              </div>
            )}
          </div>
        );
      });
  };

  return (
    <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-header-content">
          <div className="sidebar-brand">
            {empresaLogo ? (
              <img src={empresaLogo} alt="Logo" className="sidebar-logo" />
            ) : (
              <div className="sidebar-logo-placeholder">
                <ShoppingCart size={isSidebarCollapsed ? 20 : 24} />
              </div>
            )}
            {!isSidebarCollapsed && (
              <div className="sidebar-brand-text">
                <h2>VERTEX</h2>
                {empresaNombre && <span className="sidebar-empresa-nombre">{empresaNombre}</span>}
              </div>
            )}
          </div>
          <button className="sidebar-toggle-btn" onClick={toggleSidebar} title={isSidebarCollapsed ? "Expandir" : "Colapsar"}>
            {isSidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>
      </div>

      {/* Indicador de Sync - oculto en modo web */}
      {syncConfig && !isWebMode && (
        <button
          className={`sidebar-sync-indicator ${syncRunning ? 'syncing' : syncActive ? 'active' : 'inactive'} ${syncLastResult && !syncLastResult.success ? 'has-error' : ''}`}
          onClick={() => setSyncModal && setSyncModal(true)}
          title={syncRunning ? 'Sincronizando...' : syncActive ? 'Sync activo - Click para detalles' : 'Sync inactivo - Click para detalles'}
        >
          {syncRunning ? (
            <Loader size={14} className="spinning" />
          ) : (
            <Cloud size={14} />
          )}
          {!isSidebarCollapsed && (
            <span className="sidebar-sync-text">
              {syncRunning ? 'Sincronizando...' : syncActive ? 'Sync Activo' : 'Sync Inactivo'}
            </span>
          )}
          <div className={`sidebar-sync-dot ${syncRunning ? 'syncing' : syncActive ? 'active' : syncLastResult && !syncLastResult.success ? 'error' : 'inactive'}`} />
        </button>
      )}

      <nav className="sidebar-nav">
        {menuItems.length > 0 ? (
          menuItems.map((item) => {
            const Icon = item.icon;
            const isCollapsed = collapsedItems[item.id];

            return (
              <div key={item.id} className="sidebar-menu-group">
                {/* Item principal */}
                <button
                  className={`sidebar-item ${item.children
                    ? 'collapsible'
                    : (activeView === item.id ? 'active' : '')
                    }`}
                  onClick={() => {
                    if (item.children) {
                      if (!isSidebarCollapsed) {
                        toggleCollapse(item.id);
                      }
                    } else {
                      onViewChange(item.id);
                    }
                  }}
                  title={isSidebarCollapsed ? item.label : (item.permission ? `${item.label} - Requiere: ${item.permission}` : item.label)}
                >
                  {Icon && <Icon size={20} />}
                  {!isSidebarCollapsed && (
                    <>
                      <span className="sidebar-item-label">{item.label}</span>
                      {item.children && (
                        <div className="collapse-icon">
                          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>
                      )}
                    </>
                  )}
                </button>

                {/* Sub-items */}
                {item.children && !isCollapsed && !isSidebarCollapsed && (
                  <div className="sidebar-submenu">
                    {renderSubItems(item.children)}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          !isSidebarCollapsed && (
            <div className="no-permissions">
              <p>No tienes permisos para acceder a ninguna sección</p>
            </div>
          )
        )}
      </nav>

      <div className="sidebar-footer">
        <button
          className="sidebar-item logout"
          onClick={handleLogout}
          title={isSidebarCollapsed ? "Cerrar Sesión" : ""}
        >
          <LogOut size={20} />
          {!isSidebarCollapsed && <span>Cerrar Sesión</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
